// ChatWidgetController — glue layer between the CountBucks app and the
// GoHighLevel LeadConnector chat widget installed in index.html.
//
// Responsibilities:
// 1. HIDE the chat bubble on operator/staff/signer routes so it never
//    covers a signature box or the admin UI. Uses a global CSS rule
//    that targets the `<chat-widget>` custom element — the widget
//    itself doesn't need to know we're hiding it.
// 2. PREFILL the visitor's name + email from AuthContext when they're
//    signed in, so HighLevel already knows who's asking. We try three
//    approaches for compatibility with any HighLevel widget version:
//      a. window.leadConnector.chatWidget.setContact(...)  (if exposed)
//      b. dispatch a `lc:prefill` CustomEvent on the element
//      c. write to sessionStorage under the key HighLevel reads
//    Every path is defensive: if the API doesn't exist we no-op.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fireConversion } from '../lib/gtagConversions';

// Routes where the chat bubble should be hidden. Prefix match.
const HIDE_PREFIXES = [
  '/owner-admin',   // Staff dashboard
  '/sign/',         // Public signer pages (would cover signature fields)
  '/book/success',  // Post-booking confirmation
  '/portal/',       // Client portals
  '/portals/',      // Portal discovery
  '/setup-wizard',  // First-run workspace setup (keep onboarding uncluttered)
  '/login',         // Sign-up / login page — bubble covered the password field + trial CTA on mobile (iPhone)
];

const STYLE_ID = 'cb-chat-hide-style';
// Belt-and-suspenders: the LeadConnector widget renders the round bubble, the
// "Hi there!" greeting card, AND the chat panel. Depending on widget version
// these are either inside the <chat-widget> custom element OR injected as
// sibling nodes / iframes on <body>. Hide every known variant so nothing can
// cover the sign-up form or the "Start 7-day free trial" button on mobile.
const HIDE_CSS = `
  chat-widget,
  chat-widget *,
  [class*="lc_text-widget"],
  [class*="lc-text-widget"],
  [id*="lc_text-widget"],
  [class*="leadconnector"],
  [id*="leadconnector"],
  iframe[src*="leadconnector"],
  iframe[src*="leadconnectorhq"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

function shouldHide(pathname) {
  return HIDE_PREFIXES.some((p) => pathname.startsWith(p));
}

// A first-run onboarding overlay is on screen — the "Name your business"
// screen / SetupWizard dialog ([data-tour-block]) or the guided tour scrim.
// Hide the chat bubble so it never stacks on top of onboarding.
function onboardingOverlayPresent() {
  return !!document.querySelector('[data-tour-block], [data-testid="onboarding-tour-scrim"]');
}

function applyHide(hide) {
  const existing = document.getElementById(STYLE_ID);
  if (hide) {
    if (existing) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = HIDE_CSS;
    document.head.appendChild(s);
  } else if (existing) {
    existing.remove();
  }
}

function prefill(name, email) {
  if (!name && !email) return;
  const widget = document.querySelector('chat-widget');
  if (!widget) return;
  // (a) Data attributes — HighLevel's Stencil-based widget picks up
  //     prefill fields via these attributes when they're set BEFORE the
  //     first interaction (before the conversation form renders).
  try {
    if (name) widget.setAttribute('prefill-name', name);
    if (email) widget.setAttribute('prefill-email', email);
    // Legacy attribute names some widget versions read.
    if (name) widget.setAttribute('data-prefill-name', name);
    if (email) widget.setAttribute('data-prefill-email', email);
  } catch { /* noop */ }
  // (b) Programmatic API — future-proof against widget updates.
  try {
    const api = (window.leadConnector && window.leadConnector.chatWidget) || null;
    if (api && typeof api.setContact === 'function') {
      api.setContact({ name, email });
    } else if (api && typeof api.setContactInfo === 'function') {
      api.setContactInfo({ name, email });
    }
  } catch { /* noop */ }
  // (c) sessionStorage — some HighLevel widget versions read a "guest
  //     contact" blob from storage on first message. Namespaced so we
  //     don't collide with app data.
  try {
    sessionStorage.setItem('lc_guest_contact', JSON.stringify({ name, email }));
  } catch { /* storage disabled — ignore */ }
}

export default function ChatWidgetController() {
  const location = useLocation();
  const { user } = useAuth();

  // Toggle visibility whenever the route changes OR an onboarding overlay
  // appears/disappears. A MutationObserver covers the overlay case since it
  // mounts/unmounts without a route change.
  useEffect(() => {
    const sync = () => applyHide(shouldHide(location.pathname) || onboardingOverlayPresent());
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [location.pathname]);

  // Push prefill when auth resolves. Retries for up to ~10s in case the
  // widget's <chat-widget> element hasn't been created yet (loader is
  // async).
  useEffect(() => {
    if (!user?.email && !user?.name) return undefined;
    let tries = 0;
    const tryPrefill = () => {
      if (document.querySelector('chat-widget')) {
        prefill(user.name || '', user.email || '');
        return;
      }
      if (++tries < 20) setTimeout(tryPrefill, 500);
    };
    tryPrefill();
    return undefined;
  }, [user?.email, user?.name]);

  // Minimize the auto-greeting for SIGNED-IN users. The LeadConnector widget
  // pops a "Hi there! Have a question?" prompt bubble (.lc_text-widget--prompt)
  // next to its launcher. Inside the app that's noise — the user already has
  // the AI Assistant. We keep the round launcher (.lc_text-widget--bubble) but
  // hide the greeting by injecting a <style> INTO the widget's shadow root
  // (page CSS can't reach shadow DOM). Public/marketing visitors still get the
  // greeting, since this only runs when a user is authenticated.
  useEffect(() => {
    if (!user) return undefined;
    let tries = 0;
    let timer;
    const STYLE_ID = 'cb-lc-greeting-hide';
    const inject = () => {
      const cw = document.querySelector('chat-widget');
      if (cw && cw.shadowRoot) {
        if (!cw.shadowRoot.getElementById(STYLE_ID)) {
          const s = document.createElement('style');
          s.id = STYLE_ID;
          s.textContent = '.lc_text-widget--prompt{display:none !important;}';
          cw.shadowRoot.appendChild(s);
        }
        return;
      }
      if (++tries < 40) timer = setTimeout(inject, 500);
    };
    inject();
    return () => { if (timer) clearTimeout(timer); };
  }, [user]);

  // Fire a Google Ads `chat_started` conversion the FIRST time the visitor
  // opens the chat during this session. Detection is DOM-based (widget
  // exposes no callback): the LeadConnector <chat-widget> flips
  // data-active / aria-expanded / an "open" class when the panel opens.
  // We poll for the widget to appear, then observe attribute changes.
  useEffect(() => {
    let fired = false;
    let observer = null;
    let poll = null;
    const armOnce = (widget) => {
      const check = () => {
        if (fired) return;
        const opened = (
          widget.getAttribute('open') != null ||
          widget.getAttribute('data-active') === 'true' ||
          widget.getAttribute('aria-expanded') === 'true' ||
          (widget.className || '').toString().toLowerCase().includes('open')
        );
        if (opened) {
          fired = true;
          fireConversion('chat_started', {
            transactionId: `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          });
          observer?.disconnect();
        }
      };
      observer = new MutationObserver(check);
      observer.observe(widget, { attributes: true, attributeFilter: ['open', 'data-active', 'aria-expanded', 'class'] });
      check();  // in case it's already open on mount
    };
    poll = setInterval(() => {
      const w = document.querySelector('chat-widget');
      if (w) { clearInterval(poll); poll = null; armOnce(w); }
    }, 500);
    return () => {
      if (poll) clearInterval(poll);
      if (observer) observer.disconnect();
    };
  }, []);

  return null;
}
